import {
    GlobalTenantSystemContentProps,
    IAuthLevel,
    IPossibleScope,
    SetGlobalTenantSystemContentProps,
    TenantLine,
    TenantSystemContentProps
} from "../Interfaces/ITenantSystem";
import * as React from "react";
import {ChangeEvent, ReactElement, useEffect, useRef} from "react";
import {HubPathRouting} from "../../../HubFramework/pathBuilder";
import {IProject} from "../../Hub/Interfaces/IAllProjects";
import {GlobalDictionary} from "../../../Utils/globalDictionary";
import {IModule} from "../../../BackendFacade/IModuleInstances";
import {listModuleInstances} from "../../../BackendFacade/moduleCalls";
import {IListStaticModule} from "../../../BackendFacade/IStaticModules";
import {useMain} from "../../../Utils/SessionControls/mainContext";
import OdxSelect, {OdxSelectOptionProps} from "../../../OdxComponents/NativeOdx/FormControls/Odx-Select";
import {odxIconSets} from "../../../OdxComponents/constants";
import OdxIcon from "../../../OdxComponents/NativeOdx/Odx-Icon";
import {transformOdxLayoutRef} from "../../../OdxComponents/odxLayoutRef";
import OdxInputField from "../../../OdxComponents/NativeOdx/FormControls/Odx-InputField";

export enum ScopeType {
    IDMS,
    Hub
}

// todo: maybe add some utils for "user" and detecting it as default root scope

// convert generic dictionary (IAuthLevel) to detailed list of TenantLine objects
export function convertAuthlevelToTenantsystem(authlevel: IAuthLevel, currentProject: string): TenantLine[] {
    const scopes = [] as TenantLine[];

    Object.keys(authlevel).forEach((key) => {
        scopes.push({id: scopes.length, scopes: key.split('/'), value: authlevel[key], tmp: "", fullscope: key})
    });

    // add global scope if it does not exist and on hub level
    if (scopes.find(x => x.scopes[0] === "user" && x.scopes.length === 1) === undefined && currentProject === "null") {
        scopes.push({id: scopes.length, scopes: ["user"], value: "", tmp: "", fullscope: "user"}) // todo: no value?
    }

    return scopes;
}

/**
 * Load all possible scopes for the tenant management.
 * "Loading" means we add the list of available module instances (and projects) into the globalDic for the hub permission GUI later
 * @param global if true, load all projects and modules, else only the modules of the current project
 * @param createErrorToast function to create a popup, from the main context
 */
export async function loadAllTenantScope(global: boolean,
                                         createErrorToast: (title: string, message: string) => void) {
    const promises = [] as Promise<void>[];

    // all projects
    if (GlobalDictionary.get(GlobalDictionary.PROJECTS) === undefined) {
        promises.push((async () => {await GlobalDictionary.getProjects()})());
    }

    const key = GlobalDictionary.MODULEINSTANCES;

    // all modules of the project
    if (!global) {
        const moduleId = HubPathRouting.currentProject.id; // todo: WTF? that is the projectId??? or not??? its USED like it!?

        if (GlobalDictionary.get(moduleId + key) === undefined) {
            promises.push(listModuleInstances(moduleId).then((data) => {
                if (data.success) {
                    const modules = data.data.value as IModule[]
                    GlobalDictionary.set(moduleId + key, modules)
                } else {
                    createErrorToast("Error", data.message);
                    throw new Error(data.message);
                }
            }))
        }
    } else {
        // global
        if (GlobalDictionary.get(GlobalDictionary.PROJECTS) === undefined) {
            await Promise.all(promises); // wait for the getAllProjects promise we added earlier above.
        }

        GlobalDictionary.get(GlobalDictionary.PROJECTS).forEach((project: IProject) => {
            if (GlobalDictionary.get(project.projectId + key) === undefined) {
                promises.push(listModuleInstances(project.projectId).then((data) => {
                    if (data.success) {
                        const modules = data.data.value as IModule[]
                        GlobalDictionary.set(project.projectId + key, modules)
                    } else {
                        // HubFunctions.createErrorToast("Error", data.message)
                        createErrorToast("Error", data.message);
                        throw new Error(data.message);
                    }
                }))
            }
        })
    }

    await Promise.all(promises);
}

export interface IUserTenantManagementAddScopeProps {
    setTenantSystemFunctionProps: (tenantSystem: TenantLine) => void,
    tenantSystemProps: TenantLine,
    projectId: string,
    authLevels: string[],
    listOfIDMSSubscopes: string[],
    global: boolean,
    useSelectOptions: boolean,
    showAddScopeButton?: boolean,
    scopeType: ScopeType
}

/**
 *
 * @param setTenantSystemFunctionProps Setter function for the tenant system
 * @param tenantSystemProps Tenant system properties from the parent
 * @param projectId Project ID
 * @param authLevels List of all possible auth levels
 * @param listOfIDMSSubscopes List of all possible IDMS subscopes
 * @param global If true, the tenant system is used on hub global level
 * @param useSelectOptions If true, use select options for the scopes, else use input fields
 * @param showAddScopeButton
 * @constructor
 */
export const TenantManagementScope: React.FC<IUserTenantManagementAddScopeProps> = ({
                                                                                        setTenantSystemFunctionProps,
                                                                                        tenantSystemProps,
                                                                                        projectId,
                                                                                        authLevels,
                                                                                        listOfIDMSSubscopes,
                                                                                        global,
                                                                                        useSelectOptions,
                                                                                        showAddScopeButton = true,
                                                                                        scopeType
                                                                                    }) => {
    const [tenantSystem, setTenantSystem] = React.useState<TenantLine>(tenantSystemProps);
    const [staticModules, setStaticModules] = React.useState<IListStaticModule[]>([]);

    const {createErrorToast, setLoading, isLoading} = useMain();

    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // Call the transformation function after the component has updated.
        if (containerRef.current) {
            transformOdxLayoutRef(containerRef.current);
        }
    }, [tenantSystem]);

    // INIT
    React.useEffect(() => {
        //
        const isHub = scopeType == ScopeType.Hub;
        // let shouldBeHub = listOfIDMSSubscopes.length === 0;
        // if (isHub != shouldBeHub) throw new Error("invalid params");

        if (isHub) {
            loadAllPossibleScopes(global);
        }

        // if a project tenant is set, prepare the tenantsystem, so that the project level is already shown // todo: wat?
        if (showAddScopeButton && tenantSystem.scopes.length === 0) {
            addScopePart("")
        }
    }, []);

    useEffect(() => {
        setTenantSystemFunctionProps(tenantSystem);
    }, [tenantSystem]);

    useEffect(() => {
        setTenantSystem(tenantSystemProps);
    }, [tenantSystemProps]);

    const loadAllPossibleScopes = async (global: boolean) => {
        setLoading(true)
        const promises = [] as Promise<void>[]

        // adds available modules and projects into globalDic
        promises.push(loadAllTenantScope(global, createErrorToast))

        // required to list the endpoints of a module for sub-sub-scoped permissions
        promises.push(GlobalDictionary.getStaticModules().then((data) => {
            setStaticModules(data)
        }))

        await Promise.all(promises)
        setLoading(false)
    }

    /**
     * removes part of the scope from the config
     * @param scopeIndex scope index
     */
    const deleteScopePart = (scopeIndex: number) => {
        const scopes = tenantSystem.scopes;
        scopes.splice(scopeIndex, 1);
        setTenantSystem({...tenantSystem, scopes: scopes, fullscope: scopes.join("/")});
    }

    /**
     * returns a list of all possible scopes with display name which are used so far
     * @param scope value of input field
     * @param scopeId index number of scope. for hub permissions: 0 is root (useless), 1 is project level scope, 2 module, 3 endpoint
     */
    const returnListOfPossibleScopes = (scope: string, scopeId: number): IPossibleScope[] => {
        if (scopeId === 0) return []; // we have a disabled(?) dropDown for the 0th scope?? this calls this but we can never edit it because index 0 is always user/global


        if (isLoading) {
            // throw new Error("dont call this if loading isnt done. otherwise you dont know if its actually an empty list or an errr");
            return []; // todo: return error? shouldnt be called so early? make async??
        }

        // IDMS
        if (scopeType == ScopeType.IDMS) {
            if (!listOfIDMSSubscopes.length) {
                return [{id: "get scopes", displayName: "get scopes"}] as IPossibleScope[]
            }
            return listOfIDMSSubscopes
                .filter(x => x.toLowerCase().indexOf(scope.toLowerCase()) !== -1) // todo: what does this do? does listOfIDMSSubscopes contain e.g. germany/hesse/frankfurt and "scope" is germany, so we only show the subscopes of it?
                .map(x => {
                    return {id: x, displayName: x}
                });
        }

        if (scopeType != ScopeType.Hub) throw new Error("invalid scopeType");

        // Hub
        const tenant: TenantLine = tenantSystem

        switch (scopeId) {
            // project level scope
            case 1:
                const projects: IProject[] = GlobalDictionary.get(GlobalDictionary.PROJECTS) ?? []
                return projects.filter(x => x.displayName.toLowerCase().indexOf(scope.toLowerCase()) !== -1).map(x => {
                    return {id: x.projectId, displayName: x.displayName}
                })

            // module level scope
            case 2:
                let modules: IModule[] = []
                if (global) {
                    modules = GlobalDictionary.get(tenant.scopes[1] + GlobalDictionary.MODULEINSTANCES)
                } else {
                    modules = GlobalDictionary.get(HubPathRouting.currentProject.id + GlobalDictionary.MODULEINSTANCES)
                }
                if (!modules) {
                    return []
                }
                return modules.filter(x => x.displayName.toLowerCase().indexOf(scope.toLowerCase()) !== -1).map(x => {
                    return {id: x.moduleInstanceId, displayName: x.displayName}
                })

            // scope inside module
            default:
                // find the current module
                let instances: IModule[] = []
                if (global) {
                    instances = GlobalDictionary.get(tenant.scopes[1] + GlobalDictionary.MODULEINSTANCES)
                } else {
                    instances = GlobalDictionary.get(HubPathRouting.currentProject.id + GlobalDictionary.MODULEINSTANCES)
                }
                const currentModule = instances?.find(x => x.moduleInstanceId === tenant.scopes[2]).staticModuleId
                const scopes = staticModules.find(x => x.staticModuleId === currentModule)?.endpoints
                if (!scopes) {
                    return []
                }
                return Object.keys(scopes).map(x => {
                    return {id: x, displayName: x}
                });
        }
    }

    /**
     * Add a new part to a scope
     * @param scope scope value
     */
    const addScopePart = (scope: string) => {
        const scopes = tenantSystem.scopes
        // if scope does not exist in config, create it
        if (tenantSystem.scopes.length === 0)
            scopes.push("user")
        scopes.push(scope);
        const fullscope = scopes.join("/")
        setTenantSystem({...tenantSystem, scopes: scopes, fullscope: fullscope});
    }

    /**
     * returns all possible auth levels
     */
    const getAuthLevelsOptions = () => { // todo: could be renamed to "Roles": Admin, Reader, Editor, ..., right?
        if (scopeType == ScopeType.IDMS) {
            return authLevels.map(item => {
                return {value: item, label: item} as OdxSelectOptionProps
            });
        }

        // HUB case
        let res: string[] = authLevels;
        if (isOnProjectLevel()) res = res.filter(x => x != "neutral"); // "neutral" role only exists on global root level, not in projects
        return res.map(item => {
            return {value: item, label: item} as OdxSelectOptionProps
        });
    }

    /**
     * returns all possible scopes
     */
    const getScopeOptions = (id: number) => {
        // First, get the list of possible scopes as an array of option elements
        const scopeOptions = returnListOfPossibleScopes("", id).map((item, key) => {
            // return <option value={item.id} key={key}>{item.displayName}</option>;
            return {value: item.id, label: item.displayName} as OdxSelectOptionProps
        });

        // Next, create a placeholder option element
        // const placeholderOption = <option value="" key="placeholder" >---</option>;
        const placeholderOption = {value: "", label: "---"} as OdxSelectOptionProps

        // Finally, add the placeholder to the beginning of the options array
        // return [placeholderOption, ...scopeOptions];
        return [placeholderOption, ...scopeOptions];
    }

    /**
     * Edit the value of the User scope
     * @param value value
     * @param index index of the scope line
     */
    const setScopeValue = (value: string, index: number) => {
        const scopes = tenantSystem.scopes;
        scopes[index] = value;
        setTenantSystem({...tenantSystem, scopes: scopes, fullscope: scopes.join("/")});
    }

    // used for GUI placeholder labels
    const getScopeTypeDisplayName = (index: number) => {
        if (scopeType == ScopeType.IDMS) return "Tenant";

        if (index === 1) {
            return "Project"
        } else if (index === 2) {
            return "Module"
        } else if (index === 3) {
            return "Endpoint";
        } else throw new Error("index out of range");
    }

    const isOnProjectLevel = () => {
        if (scopeType == ScopeType.IDMS) return false;
        return !!HubPathRouting.currentProject;
        return projectId && projectId != "null";
    }

    const getValueField = (index: number) => {
        if (scopeType == ScopeType.Hub) {
            return <OdxSelect
                options={getScopeOptions(index)}
                value={tenantSystem.scopes[index]}
                onChange={(event: ChangeEvent<HTMLSelectElement>) => {
                    setScopeValue(event.target.value, index)
                }}
                id={"UserMng_EditUser_ScopeDropDown"}
                placeholder={getScopeTypeDisplayName(index)}
                label={getScopeTypeDisplayName(index)}
                isRequired={true}
                disabled={tenantSystem.scopes.length >= index + 2 || (HubPathRouting.currentProject !== undefined && index === 1)}
            />
        } else {
            return <OdxInputField isRequired={true}
                                  id={index.toString()}
                                  label={getScopeTypeDisplayName(index)}
                                  value={tenantSystem.scopes[index]}
                                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                      setScopeValue(event.target.value, index)
                                  }
                                  }
            />
        }
    }

    return <div ref={containerRef}>
        {/* Role Select */}
        <OdxSelect
            options={getAuthLevelsOptions()}
            value={tenantSystem.value}
            onChange={(event: ChangeEvent<HTMLSelectElement>) => {
                setTenantSystem({...tenantSystem, value: event.target.value});
            }}
            id={"UserMng_UserEdit_RoleSelectDropDown"}
            placeholder="Select Role"
            label={"Role"}
            isRequired={true}
        />
        <odx-separator style={{margin: '1rem 0'}} />

        {(showAddScopeButton && useSelectOptions) && <> {
            tenantSystem.scopes.map((scope, index) => {
                if (scope === "user" && index === 0) return <></>
                let gridCellWidth = 11
                if (index === 1) gridCellWidth = 12;

                return <div data-odxLayout={"grid"} className={"odx-padding-bottom-8"}>
                    <div data-odxLayout={gridCellWidth}>
                        {getValueField(index)}
                    </div>
                    {(index > 1) &&
                        <div data-odxLayout={'1'}
                             className={`content-center flex flex-col justify-end h-full w-full`}>
                            <odx-icon-button
                                className="mb-0 mt-[5%] float-right"
                                variant="ghost"
                                size="md"
                                icon={`${odxIconSets.core}::delete`}
                                onClick={() => deleteScopePart(index)}
                            />
                        </div>
                    }
                </div>
            })}
            {(scopeType === ScopeType.IDMS || tenantSystem.scopes.length < 3) &&
                <odx-button
                    variant="ghost"
                    size="sm"
                    type="button"
                    onClick={() => {
                        addScopePart("")
                    }}
                    disabled={tenantSystem.scopes[tenantSystem.scopes.length - 1] === "" ? true : undefined}
                >
                    <odx-icon slot="prefix" name={`${odxIconSets.core}::plus`} size="sm"></odx-icon>
                    Add Sub Scope
                </odx-button>
            }
        </>
        }
    </div>;
}
